/**
 * client/network.ts
 * Responsabilidad: Gestión autoritativa de conexión al servidor Colyseus, envío de mensajes y suscripción de estado.
 *
 * NOTA DE ARQUITECTURA (Schemas vs Interfaces):
 * El cliente NO importa src/schemas/*.ts para evitar incluir dependencias de servidor en el bundle del navegador.
 * references/client-sdk.md del skill indica: "The room state is automatically synchronized... The room.state property
 * always contains the latest state. Use Callbacks to listen for specific property changes with fine-grained control."
 * Por ello, definimos interfaces locales tipadas de solo lectura para interactuar con Callbacks.
 */

import { Client, Room, Callbacks } from "@colyseus/sdk";
import type { ElementalHitPayload } from "./types";

/** Nombre de la sala Colyseus a la que se une el cliente */
export const ROOM_NAME = "alchem_arena";
export const TRIAL_ROOM_NAME = "trial_arena";

/** Interfaz del estado de red de un jugador sincronizado por el servidor */
export interface IPlayerNetworkState {
    x: number;
    y: number;
    targetX: number;
    targetY: number;
    mass: number;
    element: string;
    connected: boolean;
    alive: boolean;
    respawnAt: number;
    isBot: boolean;
    kills: number;
    deaths: number;
    alias: string;

    // Power-ups activos
    invulnerableUntil: number;
    speedBoostUntil: number;
    hasShield: boolean;
    doubleMassUntil: number;
    frozenUntil: number;
    freezeCooldownUntil: number;

    // Gameplay y Personalizacion
    skin: string;
    lastElementChangeAt: number;
    killstreak: number;
    isBoosting: boolean;
    isSpectator: boolean;

    // Sprint 29: Pociones y Consumibles Tácticos
    drinkingUntil: number;
    magnetUntil: number;
    elementLockedUntil: number;
    potionCooldownUntil: number;
    activePotionEffect: string;
    potionSlot0: string;
    potionSlot1: string;
    potionSlot2: string;
    cosmicFoodUntil?: number;
    rebirthUntil?: number;
    equippedTitle?: string;

    // Sistema de Poderes y Afinidad de Mascotas
    petBuffActive?: boolean;
    petBuffType?: string;
    petBuffUntil?: number;
    petNextBuffAt?: number;
}

/** Interfaz del estado de red de una comida sincronizada por el servidor */
export interface IFoodNetworkState {
    x: number;
    y: number;
}

/** Interfaz del estado de red de un power-up */
export interface IPowerUpNetworkState {
    x: number;
    y: number;
    type: string;
}

/** Callbacks para eventos del ciclo de vida y sincronización de jugadores y comidas */
export interface NetworkStateHandlers {
    onPlayerAdd?: (sessionId: string, player: IPlayerNetworkState) => void;
    onPlayerRemove?: (sessionId: string) => void;
    onPlayerChange?: (sessionId: string, field: keyof IPlayerNetworkState, value: unknown) => void;
    onFoodAdd?: (foodId: string, food: IFoodNetworkState) => void;
    onFoodRemove?: (foodId: string) => void;
    onFoodChange?: (foodId: string, field: keyof IFoodNetworkState, value: unknown) => void;
    onPowerUpAdd?: (powerUpId: string, powerUp: IPowerUpNetworkState) => void;
    onPowerUpRemove?: (powerUpId: string) => void;
    onStateChange?: () => void;
    onElementalHit?: (payload: ElementalHitPayload) => void;
    onPowerUpCollected?: (payload: { playerId: string; type: string; x: number; y: number }) => void;
    onShieldBreak?: (payload: { playerId: string; x: number; y: number }) => void;
    onFreezeBurst?: (payload: { casterId: string; x: number; y: number; radius: number; frozenPlayerIds: string[] }) => void;
    onEventWarning?: (payload: EventWarningPayload) => void;
    onEventStart?: (payload: EventStartPayload) => void;
    onEventEnd?: (payload: { type: string }) => void;
    onComboHit?: (payload: { playerId: string; alias: string; combo: number; bonusMass: number; x: number; y: number }) => void;
    onKillstreakTier?: (payload: { playerId: string; alias: string; streak: number; title: string; emoji: string; color: string; x: number; y: number }) => void;
    onKillstreakShutdown?: (payload: { killerId: string; killerAlias: string; victimId: string; victimAlias: string; endedStreak: number; bountyMass: number; x: number; y: number }) => void;
    onEssenceBurst?: (payload: EssenceBurstPayload) => void;
    onTitleUnlocked?: (payload: { playerId: string; alias: string; titleId: string; titleName: string; icon: string; primaryColor: string; x: number; y: number }) => void;
}

export interface EssenceBurstPayload {
    x: number;
    y: number;
    radius?: number;
    element: string;
    alias?: string;
    massDropped: number;
    reason: "evasion" | "bot_retire";
}

export interface EventWarningPayload {
    type: string;
    name: string;
    emoji: string;
    color: string;
    isPositive: boolean;
    startsInMs: number;
}

export interface EventStartPayload {
    type: string;
    name: string;
    emoji: string;
    color: string;
    isPositive: boolean;
    x: number;
    y: number;
    radius: number;
    durationMs: number;
}

/**
 * Crea una instancia del cliente de Colyseus con soporte para autenticación y JWT.
 */
export function createAuthClient(): Client {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host || "localhost:2567";
    const serverUrl = `${protocol}//${host}`;
    return new Client(serverUrl);
}

/** Establece conexión con el servidor Colyseus y se une o crea la sala del juego. */
export async function connectToServer(
    alias: string,
    spectator: boolean = false,
    inviteCode?: string,
    token?: string,
    privateBotCount?: number
): Promise<Room> {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host || "localhost:2567";
    const serverUrl = `${protocol}//${host}`;

    try {
        const client = new Client(serverUrl);
        const joinOptions: { alias: string; spectator: boolean; inviteCode?: string; token?: string; privateBotCount?: number } = {
            alias,
            spectator,
        };
        if (inviteCode && inviteCode.length > 0) {
            joinOptions.inviteCode = inviteCode;
        }
        if (token && token.length > 0) {
            joinOptions.token = token;
        }
        if (typeof privateBotCount === "number") {
            joinOptions.privateBotCount = privateBotCount;
        }
        const room = await client.joinOrCreate(ROOM_NAME, joinOptions);
        console.log(`[Network] Conectado exitosamente a la sala '${ROOM_NAME}' (${room.roomId}) inviteCode=${inviteCode ?? "public"}`);
        return room;
    } catch (error) {
        console.error("[Network] Error crítico al conectar con el servidor:", error);
        throw new Error(`No se pudo conectar a la sala '${ROOM_NAME}': ${(error as Error).message}`);
    }
}

/** Establece conexión con el servidor Colyseus para una Arena de Desafío Diario (TrialRoom). */
export async function connectToTrialRoom(
    trialId: string,
    partyCode?: string,
    isCoop?: boolean,
    token?: string,
    alias?: string,
    userId?: string,
    skin?: string
): Promise<Room> {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host || "localhost:2567";
    const serverUrl = `${protocol}//${host}`;

    try {
        const client = new Client(serverUrl);
        const cleanParty = partyCode ? partyCode.trim().toUpperCase() : "";
        const coopFlag = Boolean(isCoop || cleanParty.length > 0 || trialId === "trial_coop_purge");
        const instanceId = coopFlag && cleanParty
            ? cleanParty
            : `solo_${userId || "anon"}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

        const joinOptions = {
            trialId,
            partyCode: cleanParty,
            isCoop: coopFlag ? 1 : 0,
            instanceId,
            token: token || "",
            alias: alias || "Alquimista",
            userId: userId || "",
            skin: skin || "default",
        };

        const room = await client.joinOrCreate(TRIAL_ROOM_NAME, joinOptions);
        console.log(`[Network] Conectado a '${TRIAL_ROOM_NAME}' (${room.roomId}) trialId=${trialId} party=${cleanParty || "solo"}`);
        return room;
    } catch (error) {
        console.error("[Network] Error al conectar con trial_arena:", error);
        throw new Error(`No se pudo conectar a '${TRIAL_ROOM_NAME}': ${(error as Error).message}`);
    }
}

/** Envía un mensaje autoritativo de intención de movimiento hacia coordenadas del mundo. */
export function sendMove(room: Room, targetX: number, targetY: number): void {
    if (!room) {
        throw new Error("No hay una sala activa para enviar el mensaje de movimiento.");
    }
    room.send("move", { targetX, targetY });
}

/** Envía el estado de la barra espaciadora para activar o desactivar la propulsión alquímica. */
export function sendBoost(room: Room, active: boolean): void {
    if (!room) return;
    room.send("boost", { active });
}

/** Sprint 29: Envía la acción de consumir una poción en la ranura indicada (0, 1 o 2). */
export function sendUsePotion(room: Room, slot: number): void {
    if (!room) return;
    room.send("use-potion", { slot });
}

/** Registra observadores reactivos para altas, bajas y cambios de propiedades de jugadores. */
export function onRoomStateChange(room: Room, handlers: NetworkStateHandlers): void {
    const callbacks = Callbacks.get(room);

    callbacks.onAdd("players", (player: any, sessionId: any) => {
        const state = player as IPlayerNetworkState;
        handlers.onPlayerAdd?.(String(sessionId), state);

        callbacks.listen(player, "x", (val) => handlers.onPlayerChange?.(String(sessionId), "x", val));
        callbacks.listen(player, "y", (val) => handlers.onPlayerChange?.(String(sessionId), "y", val));
        callbacks.listen(player, "mass", (val) => handlers.onPlayerChange?.(String(sessionId), "mass", val));
        callbacks.listen(player, "element", (val) => handlers.onPlayerChange?.(String(sessionId), "element", val));
        callbacks.listen(player, "connected", (val) => handlers.onPlayerChange?.(String(sessionId), "connected", val));
        callbacks.listen(player, "alive", (val) => handlers.onPlayerChange?.(String(sessionId), "alive", val));
        callbacks.listen(player, "respawnAt", (val) => handlers.onPlayerChange?.(String(sessionId), "respawnAt", val));
        callbacks.listen(player, "isBot", (val) => handlers.onPlayerChange?.(String(sessionId), "isBot", val));
        callbacks.listen(player, "kills", (val) => handlers.onPlayerChange?.(String(sessionId), "kills", val));
        callbacks.listen(player, "deaths", (val) => handlers.onPlayerChange?.(String(sessionId), "deaths", val));
        callbacks.listen(player, "alias", (val) => handlers.onPlayerChange?.(String(sessionId), "alias", val));
        callbacks.listen(player, "invulnerableUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "invulnerableUntil", val));
        callbacks.listen(player, "speedBoostUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "speedBoostUntil", val));
        callbacks.listen(player, "hasShield", (val) => handlers.onPlayerChange?.(String(sessionId), "hasShield", val));
        callbacks.listen(player, "doubleMassUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "doubleMassUntil", val));
        callbacks.listen(player, "frozenUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "frozenUntil", val));
        callbacks.listen(player, "freezeCooldownUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "freezeCooldownUntil", val));
        callbacks.listen(player, "skin", (val) => handlers.onPlayerChange?.(String(sessionId), "skin", val));
        callbacks.listen(player, "lastElementChangeAt", (val) => handlers.onPlayerChange?.(String(sessionId), "lastElementChangeAt", val));
        callbacks.listen(player, "killstreak", (val) => handlers.onPlayerChange?.(String(sessionId), "killstreak", val));
        callbacks.listen(player, "isBoosting", (val) => handlers.onPlayerChange?.(String(sessionId), "isBoosting", val));
        callbacks.listen(player, "isSpectator", (val) => handlers.onPlayerChange?.(String(sessionId), "isSpectator", val));
        callbacks.listen(player, "drinkingUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "drinkingUntil", val));
        callbacks.listen(player, "magnetUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "magnetUntil", val));
        callbacks.listen(player, "elementLockedUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "elementLockedUntil", val));
        callbacks.listen(player, "potionCooldownUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "potionCooldownUntil", val));
        callbacks.listen(player, "activePotionEffect", (val) => handlers.onPlayerChange?.(String(sessionId), "activePotionEffect", val));
        callbacks.listen(player, "potionSlot0", (val) => handlers.onPlayerChange?.(String(sessionId), "potionSlot0", val));
        callbacks.listen(player, "potionSlot1", (val) => handlers.onPlayerChange?.(String(sessionId), "potionSlot1", val));
        callbacks.listen(player, "potionSlot2", (val) => handlers.onPlayerChange?.(String(sessionId), "potionSlot2", val));
        callbacks.listen(player, "cosmicFoodUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "cosmicFoodUntil", val));
        callbacks.listen(player, "rebirthUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "rebirthUntil", val));
        callbacks.listen(player, "equippedTitle", (val) => handlers.onPlayerChange?.(String(sessionId), "equippedTitle", val));

        // Sistema de Poderes y Afinidad de Mascotas
        callbacks.listen(player, "petBuffActive", (val) => handlers.onPlayerChange?.(String(sessionId), "petBuffActive", val));
        callbacks.listen(player, "petBuffType", (val) => handlers.onPlayerChange?.(String(sessionId), "petBuffType", val));
        callbacks.listen(player, "petBuffUntil", (val) => handlers.onPlayerChange?.(String(sessionId), "petBuffUntil", val));
        callbacks.listen(player, "petNextBuffAt", (val) => handlers.onPlayerChange?.(String(sessionId), "petNextBuffAt", val));
    });

    callbacks.onRemove("players", (_player: any, sessionId: any) => {
        handlers.onPlayerRemove?.(String(sessionId));
    });

    callbacks.onAdd("foods", (food: any, foodId: any) => {
        const state = food as IFoodNetworkState;
        handlers.onFoodAdd?.(String(foodId), state);
        callbacks.listen(food, "x", (val) => handlers.onFoodChange?.(String(foodId), "x", val));
        callbacks.listen(food, "y", (val) => handlers.onFoodChange?.(String(foodId), "y", val));
    });

    callbacks.onRemove("foods", (_food: any, foodId: any) => {
        handlers.onFoodRemove?.(String(foodId));
    });

    callbacks.onAdd("powerups", (powerUp: any, powerUpId: any) => {
        const state = powerUp as IPowerUpNetworkState;
        handlers.onPowerUpAdd?.(String(powerUpId), state);
    });

    callbacks.onRemove("powerups", (_powerUp: any, powerUpId: any) => {
        handlers.onPowerUpRemove?.(String(powerUpId));
    });

    if (handlers.onStateChange) {
        room.onStateChange(() => handlers.onStateChange?.());
    }

    room.onMessage("elemental-hit", (payload: ElementalHitPayload) => {
        handlers.onElementalHit?.(payload);
    });

    room.onMessage("powerup-collected", (payload: { playerId: string; type: string; x: number; y: number }) => {
        handlers.onPowerUpCollected?.(payload);
    });

    room.onMessage("shield-break", (payload: { playerId: string; x: number; y: number }) => {
        handlers.onShieldBreak?.(payload);
    });

    room.onMessage("freeze-burst", (payload: { casterId: string; x: number; y: number; radius: number; frozenPlayerIds: string[] }) => {
        handlers.onFreezeBurst?.(payload);
    });

    room.onMessage("event-warning", (payload: EventWarningPayload) => {
        console.log("[Event] Mensaje event-warning llegó por red:", payload);
        handlers.onEventWarning?.(payload);
    });
    room.onMessage("event-start", (payload: EventStartPayload) => {
        handlers.onEventStart?.(payload);
    });
    room.onMessage("event-end", (payload: { type: string }) => {
        handlers.onEventEnd?.(payload);
    });

    room.onMessage("combo-hit", (payload: { playerId: string; alias: string; combo: number; bonusMass: number; x: number; y: number }) => {
        handlers.onComboHit?.(payload);
    });
    room.onMessage("killstreak-tier", (payload: { playerId: string; alias: string; streak: number; title: string; emoji: string; color: string; x: number; y: number }) => {
        handlers.onKillstreakTier?.(payload);
    });
    room.onMessage("killstreak-shutdown", (payload: { killerId: string; killerAlias: string; victimId: string; victimAlias: string; endedStreak: number; bountyMass: number; x: number; y: number }) => {
        handlers.onKillstreakShutdown?.(payload);
    });
    room.onMessage("essence-burst", (payload: EssenceBurstPayload) => {
        handlers.onEssenceBurst?.(payload);
    });
    room.onMessage("title-unlocked", (payload: { playerId: string; alias: string; titleId: string; titleName: string; icon: string; primaryColor: string; x: number; y: number }) => {
        handlers.onTitleUnlocked?.(payload);
    });
}

/** Obtiene el identificador de sesión asignado al cliente local en la sala. */
export function getLocalSessionId(room: Room): string {
    return room.sessionId;
}
